import React from 'react';
import { Form, Input } from 'antd';

import { FieldAirline } from 'leash/components/fields/fieldAirline/FieldAirline';
import { FieldAirport } from 'leash/components/fields/fieldAirport/FieldAirport';
import { FieldGroundTransporter } from 'leash/components/fields/fieldGroundTransporter/FieldGroundTransporter';
import { FieldExchangeNumbers } from 'leash/components/fields/fieldExchangeNumbers/FieldExchangeNumbers';
import { FieldChaperone } from 'leash/components/fields/fieldChaperone/FieldChaperone';
import { FieldTransporterCompany } from 'leash/components/fields/fieldTransporterCompany/FieldTransporterCompany';
import { FieldDate } from 'shared/components/fields/fieldDate/FieldDate';
import { FieldTime } from 'shared/components/fields/fieldTime/FieldTime';
import FieldPhone from 'shared/components/fields/fieldPhone/FieldPhone';
import { FieldState } from 'shared/components/fields/fieldState/FieldState';
import { Nullable, SequenceChangesType, SequenceTypesIds } from 'types';

import { GridEmptyRow } from './Sequences.styles';

interface Props {
  sequenceType: Nullable<SequenceTypesIds>;
  index: number;
  onChanges: (e: number, type: SequenceChangesType) => void;
}

export function FormFieldsSequence({ sequenceType, index, onChanges }: Props) {
  const requiredRule = [{ required: true, message: 'This field is required' }];
  const commonFields = (prefix: string) => (
    <>
      <FieldDate
        name={`departureDate${prefix}${index}`}
        label="Departure date"
        placeholder="Select the departure date"
      />
      <FieldTime
        name={`departureTime${prefix}${index}`}
        label="Departure time"
        placeholder="Select the departure time"
      />
      <FieldDate
        name={`arrivalDate${prefix}${index}`}
        label="Arrival date"
        placeholder="Select the arrival date"
      />
      <FieldTime
        name={`arrivalTime${prefix}${index}`}
        label="Arrival time"
        placeholder="Select the arrival time"
      />
    </>
  );

  const airportDatesFields = (prefix: string) => (
    <>
      <FieldAirport
        name={`departureAirport${prefix}${index}`}
        type="origin"
        label="Departure airport"
        placeholder="Select the departure airport"
        required={true}
      />
      <FieldDate
        name={`departureDateFlight${prefix}${index}`}
        label="Departure date"
        placeholder="Select the departure date"
        required={true}
      />
      <FieldTime
        name={`departureTimeFlight${prefix}${index}`}
        label="Departure time"
        placeholder="Select the departure time"
        required={true}
      />
      <GridEmptyRow />
      <FieldAirport
        name={`arrivalAirport${prefix}${index}`}
        type="destination"
        label="Arrival airport"
        placeholder="Select the arrival airport"
        required={true}
      />
      <FieldDate
        name={`arrivalDateFlight${prefix}${index}`}
        label="Arrival date"
        placeholder="Select the arrival date"
        required={true}
      />
      <FieldTime
        name={`arrivalTimeFlight${prefix}${index}`}
        label="Arrival time"
        placeholder="Select the arrival time"
        required={true}
      />
    </>
  );

  switch (sequenceType) {
    case SequenceTypesIds.GroundTransportationSequence: {
      return (
        <>
          <FieldGroundTransporter
            name={`companyGround${index}`}
            label="Company"
            placeholder="Select one option"
            onChange={(e) => onChanges(e, 'GroundTransportation')}
          />
          <FieldPhone
            name={`companyPhone${index}`}
            label="Company phone"
            placeholder=""
            disabled={true}
            rules={[{ required: false }]}
          />
          <Form.Item name={`companyNotes${index}`} label="Company notes">
            <Input placeholder="" disabled />
          </Form.Item>
          {commonFields('Ground')}
          <Form.Item name={`driverName${index}`} label="Driver name" rules={requiredRule}>
            <Input placeholder="Type driver’s name" />
          </Form.Item>
        </>
      );
    }
    case SequenceTypesIds.FlightSequence: {
      return (
        <>
          <FieldAirline
            label="Airline"
            name={`AirlineIdFlight${index}`}
            placeholder="Select one option"
            onChange={(e) => onChanges(e, 'Airline')}
          />
          <FieldPhone
            name={`airlinePhone${index}`}
            label="Airline phone"
            placeholder=""
            disabled={true}
          />
          <Form.Item name={`airlineNotes${index}`} label="Airline notes">
            <Input placeholder="" disabled />
          </Form.Item>

          <Form.Item
            name={`flightNumber${index}`}
            label="Flight number"
            rules={requiredRule}
          >
            <Input placeholder="Type number here" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name={`waybill${index}`}
            label="Air waybill number"
            rules={requiredRule}
          >
            <Input placeholder="Type code here" />
          </Form.Item>
          <Form.Item name={`confirmationNumber${index}`} label="Confirmation number">
            <Input placeholder="Type number here" style={{ width: '100%' }} />
          </Form.Item>
          <GridEmptyRow />
          {airportDatesFields('Flight')}
        </>
      );
    }
    case SequenceTypesIds.TransporterSequence: {
      return (
        <>
          <FieldTransporterCompany
            name={`TransporterCompany${index}`}
            onChange={(e) => onChanges(parseInt(e), 'Transporter')}
            required
          />
          <FieldPhone
            name={`TransporterPhone${index}`}
            label="Transporter phone"
            placeholder="Type phone number"
            rules={[{ required: false }]}
            disabled={true}
          />
          <GridEmptyRow />
          {commonFields('Transporter')}
        </>
      );
    }
    case SequenceTypesIds.MeetupSequence:
    default: {
      return (
        <>
          <Form.Item name={`meetupLocation${index}`} label="Address" rules={requiredRule}>
            <Input placeholder="Type the address" />
          </Form.Item>

          <Form.Item name={`meetupLocation2${index}`} label="Address 2">
            <Input placeholder="Type the address" />
          </Form.Item>
          <GridEmptyRow />

          <Form.Item name={`city${index}`} label="City">
            <Input placeholder="Type the city" />
          </Form.Item>

          <FieldState name={`state${index}`} label="State" required={false} />
          <Form.Item name={`postalCode${index}`} label="Postal code">
            <Input placeholder="Type the postal code" />
          </Form.Item>
          <FieldExchangeNumbers index={index} />
          <Form.Item name={`meetupLocationName${index}`} label="Pickup Location Name">
            <Input placeholder="Type the name of the location" />
          </Form.Item>
          <FieldDate
            name={`meetupDate${index}`}
            label="Meetup date"
            required={true}
            placeholder="Select the Meetup date"
          />
          <FieldTime
            name={`meetupTimeOfDay${index}`}
            label="Meetup time"
            required={true}
            placeholder="Select the meetup time"
          />
        </>
      );
    }
    case SequenceTypesIds.PuppyChaperoneFlightSequence: {
      return (
        <>
          <FieldChaperone
            name={`chaperoneName${index}`}
            label="Chaperone name"
            onChange={(e) => onChanges(e, 'Chaperone')}
            required={true}
          />
          <FieldPhone
            name={`chaperonePhone${index}`}
            label="Chaperone phone"
            placeholder="Type the phone number here"
            disabled={true}
            required={false}
          />
          <FieldPhone
            name={`additionalPhone${index}`}
            label="Chaperone additional phone"
            placeholder="Type the phone number here"
            rules={[{ required: false }]}
          />
          <FieldAirline
            label="Airline"
            name={`AirlineIdChaperone${index}`}
            placeholder="Select one option"
          />
          <Form.Item
            name={`flightNumberChaperone${index}`}
            label="Flight number"
            rules={requiredRule}
          >
            <Input placeholder="Type number here" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name={`recordLocator${index}`}
            label="Record locator"
            required={false}
          >
            <Input placeholder="Type code here" />
          </Form.Item>
          <GridEmptyRow />
          {airportDatesFields('Chaperone')}
        </>
      );
    }
  }
}
